import React from 'react';
import { useNavigate } from 'react-router-dom';
import { usePublications } from '../../contexts/PublicationsContext';
import { useStudents } from '../../contexts/StudentsContext';
import DashboardLayout from '../../components/includes/DashboardLayout';
import {
  Users,
  GraduationCap,
  FileText,
  Activity,
  BookOpen,
  UserCheck,
  FileBarChart,
  ArrowUpRight,
  Clock,
  Calendar
} from 'lucide-react';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { ScrollArea } from "../../components/ui/scroll-area";

const Dashboard: React.FC = () => {
  const { publications, getPublishedPublications } = usePublications();
  const { students } = useStudents();
  const navigate = useNavigate();

  const publishedPublications = getPublishedPublications();

  // Calculate real stats from students data
  const totalStudents = students.length;
  const graduatedStudents = students.filter(s => s.statut === 'diplome').length;
  const currentYear = new Date().getFullYear();
  const graduatedThisYear = students.filter(s =>
    s.statut === 'diplome' && s.updatedAt.getFullYear() === currentYear
  ).length;

  // Calculate new students this month
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
  const newStudentsThisMonth = students.filter(s => s.createdAt > oneMonthAgo).length;

  const stats = [
    {
      title: 'Étudiants Inscrits',
      value: totalStudents.toString(),
      icon: <GraduationCap className="h-4 w-4 text-muted-foreground" />,
      change: `+${newStudentsThisMonth}`,
      trend: 'up'
    },
    {
      title: 'Professeurs Actifs',
      value: '24',
      icon: <UserCheck className="h-4 w-4 text-muted-foreground" />,
      change: '+2%',
      trend: 'up'
    },
    {
      title: 'Publications',
      value: publications.length.toString(),
      icon: <FileText className="h-4 w-4 text-muted-foreground" />,
      change: `+${publications.filter(p => {
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
        return p.createdAt > oneWeekAgo;
      }).length}`,
      trend: 'up'
    },
    {
      title: `Diplômés ${currentYear}`,
      value: graduatedThisYear.toString(),
      icon: <Users className="h-4 w-4 text-muted-foreground" />,
      change: `${graduatedStudents} total`,
      trend: 'up'
    }
  ];

  // Generate recent activities from real data
  const recentActivities = [
    // Recent students
    ...students
      .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
      .slice(0, 2)
      .map(student => ({
        id: `student-${student.id}`,
        action: 'Nouvel étudiant inscrit',
        user: `${student.prenom} ${student.nom}`,
        time: formatTimeAgo(student.createdAt),
        bg: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
      })),
    // Recent publications
    ...publishedPublications
      .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
      .slice(0, 2)
      .map(pub => ({
        id: `pub-${pub.id}`,
        action: 'Publication ajoutée',
        user: pub.title.substring(0, 40) + (pub.title.length > 40 ? '...' : ''),
        time: formatTimeAgo(pub.createdAt),
        bg: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
      }))
  ].slice(0, 4);

  // Helper function to format time ago
  function formatTimeAgo(date: Date): string {
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMs / 60000);
    const diffInHours = Math.floor(diffInMs / 3600000);
    const diffInDays = Math.floor(diffInMs / 86400000);

    if (diffInMinutes < 60) {
      return diffInMinutes <= 1 ? 'Il y a 1 minute' : `Il y a ${diffInMinutes} minutes`;
    } else if (diffInHours < 24) {
      return diffInHours === 1 ? 'Il y a 1 heure' : `Il y a ${diffInHours} heures`;
    } else {
      return diffInDays === 1 ? 'Il y a 1 jour' : `Il y a ${diffInDays} jours`;
    }
  }


  return (
    <DashboardLayout>
      <div className="flex-1 space-y-4 p-4 pt-6">
        <div className="flex items-center justify-between space-y-2">
          <h2 className="text-3xl font-bold tracking-tight">Tableau de Bord</h2>
          <div className="flex items-center space-x-2">
            <Button>
              <ArrowUpRight className="mr-2 h-4 w-4" />
              Exporter
            </Button>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {stats.map((stat, index) => (
            <Card key={index}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  {stat.title}
                </CardTitle>
                {stat.icon}
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stat.value}</div>
                <p className="text-xs text-muted-foreground">
                  <span className="text-green-500 font-medium">{stat.change}</span> depuis le mois dernier
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-7">
          <Card className="col-span-4">
            <CardHeader>
              <CardTitle>Publications Récentes</CardTitle>
              <CardDescription>
                Vous avez {publishedPublications.length} publication{publishedPublications.length !== 1 ? 's' : ''} publiée{publishedPublications.length !== 1 ? 's' : ''} au total.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[350px] pr-4">
                <div className="space-y-4">
                  {publishedPublications.slice(0, 5).map((publication) => (
                    <div key={publication.id} className="flex items-center justify-between p-4 border rounded-lg bg-card hover:bg-accent/50 transition-colors">
                      <div className="flex items-start space-x-4">
                        <div className="bg-primary/10 p-2 rounded-full">
                          <FileText className="h-5 w-5 text-primary" />
                        </div>
                        <div className="space-y-1">
                          <p className="text-sm font-medium leading-none">{publication.title}</p>
                          <p className="text-xs text-muted-foreground">
                            {publication.category}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center text-xs text-muted-foreground">
                        <Calendar className="mr-1 h-3 w-3" />
                        {publication.createdAt.toLocaleDateString('fr-FR')}
                      </div>
                    </div>
                  ))}
                  {publishedPublications.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      Aucune publication trouvée
                    </div>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>

          <Card className="col-span-3">
            <CardHeader>
              <CardTitle>Activités Récentes</CardTitle>
              <CardDescription>
                Dernières actions sur la plateforme
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-8">
                {recentActivities.map((activity) => (
                  <div key={activity.id} className="flex items-center">
                    <div className={`mr-4 rounded-full p-2 ${activity.bg}`}>
                      <Activity className="h-4 w-4" />
                    </div>
                    <div className="space-y-1 flex-1">
                      <p className="text-sm font-medium leading-none">{activity.action}</p>
                      <p className="text-xs text-muted-foreground">
                        {activity.user}
                      </p>
                    </div>
                    <div className="ml-auto font-medium text-xs text-muted-foreground flex items-center">
                      <Clock className="mr-1 h-3 w-3" />
                      {activity.time}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Button
            variant="outline"
            className="h-24 flex flex-col items-center justify-center space-y-2 hover:border-primary hover:text-primary transition-colors"
            onClick={() => navigate('/publications')}
          >
            <FileText className="h-8 w-8" />
            <span>Gérer Publications</span>
          </Button>
          <Button
            variant="outline"
            className="h-24 flex flex-col items-center justify-center space-y-2 hover:border-green-600 hover:text-green-600 transition-colors"
            onClick={() => navigate('/students')}
          >
            <GraduationCap className="h-8 w-8" />
            <span>Gérer Étudiants</span>
          </Button>
          <Button
            variant="outline"
            className="h-24 flex flex-col items-center justify-center space-y-2 hover:border-purple-600 hover:text-purple-600 transition-colors"
            onClick={() => navigate('/courses')}
          >
            <BookOpen className="h-8 w-8" />
            <span>Gérer Cours</span>
          </Button>
          <Button
            variant="outline"
            className="h-24 flex flex-col items-center justify-center space-y-2 hover:border-orange-600 hover:text-orange-600 transition-colors"
            onClick={() => navigate('/reports')}
          >
            <FileBarChart className="h-8 w-8" />
            <span>Voir Rapports</span>
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
